/**
 * VideoPlayer component - Simple video player with basic controls
 */

import { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface VideoPlayerProps {
  src: string;
  poster?: string; // Thumbnail image
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
}

export const VideoPlayer = ({
  src,
  poster,
  className,
  autoPlay = false,
  loop = false,
  muted = true,
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(muted);
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear any existing timeout
  const clearHideTimeout = () => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
  };

  // Show controls and schedule auto-hide if playing
  const showControlsTemporarily = () => {
    setShowControls(true);
    clearHideTimeout();

    // Only auto-hide if video is currently playing
    // Use a ref check to get the current playing state
    if (videoRef.current && !videoRef.current.paused) {
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }
  };

  // Auto-hide controls when video starts playing
  useEffect(() => {
    if (isPlaying) {
      // Show controls initially, then hide after 2 seconds
      setShowControls(true);
      clearHideTimeout();
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2000);
    } else {
      // When paused, always show controls
      clearHideTimeout();
      setShowControls(true);
    }

    return () => clearHideTimeout();
  }, [isPlaying]);

  const togglePlay = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;

    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVideoClick = () => {
    // If playing, show controls temporarily, otherwise toggle play
    if (isPlaying) {
      showControlsTemporarily();
    } else {
      togglePlay();
    }
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
  };

  return (
    <div
      className={cn('relative overflow-hidden bg-black', className)}
      onMouseEnter={() => showControlsTemporarily()}
      onMouseLeave={() => setShowControls(false)}
      onMouseMove={() => showControlsTemporarily()}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline
        onClick={handleVideoClick}
        onPlay={handleVideoPlay}
        onPause={handleVideoPause}
        onEnded={handleVideoEnded}
        className="h-full w-full cursor-pointer object-contain"
      />

      {/* Play/Pause overlay */}
      {showControls && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity"
          onClick={handleVideoClick}
        >
          <Button
            size="icon"
            variant="ghost"
            className="h-16 w-16 rounded-full bg-black/60 text-white hover:bg-black/80 hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              togglePlay();
            }}
          >
            {isPlaying ? <Pause className="h-8 w-8" /> : <Play className="ml-1 h-8 w-8" />}
          </Button>
        </div>
      )}

      {/* Controls bar */}
      {showControls && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
          <div className="flex items-center justify-between">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-white hover:bg-white/20 hover:text-white"
              onClick={toggleMute}
            >
              {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      )}

      {/* Video type indicator (when not playing) */}
      {!isPlaying && (
        <div className="absolute right-2 top-2 rounded bg-black/60 px-2 py-1 text-xs font-semibold text-white">
          VIDEO
        </div>
      )}
    </div>
  );
};
